import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import React from "react";
import { decodeHashedObject, hashObject } from "../helpers/utils";
type SearchParamKey =
  | "active-tab"
  | "pageNumber"
  | "policy-status"
  | "applicationReference"
  | "postLoginRedirectionUrl";
type QuerySearchParam = {
  key: SearchParamKey;
  value: string | Record<string, unknown>;
  isHashed?: boolean;
};

function useClientQueryString() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const append = React.useCallback(
    (queriesList: QuerySearchParam[], newPathname?: string) => {
      const params = new URLSearchParams([...(searchParams ?? [])]);
      queriesList.forEach(({ key, value }) => {
        if (typeof value === "string") params.append(key, value);
      });
      const searchString = params.size > 0 ? `?${params.toString()}` : "";
      navigate(`${newPathname ?? pathname}${searchString}`);
    },
    [navigate, searchParams, pathname],
  );

  const set = React.useCallback(
    (queriesList: QuerySearchParam[], newPathname?: string) => {
      const params = new URLSearchParams([...(searchParams ?? [])]);
      queriesList.forEach(({ key, value, isHashed }) => {
        if (typeof value === "string" && !isHashed) {
          params.set(key, value);
          return;
        }
        if (typeof value === "object" && isHashed)
          params.set(key, hashObject(value) ?? "");
      });
      const searchString = params.size > 0 ? `?${params.toString()}` : "";
      navigate(`${newPathname ?? pathname}${searchString}`);
    },
    [navigate, searchParams, pathname],
  );

  const setHashed = React.useCallback(
    (
      key: SearchParamKey,
      value: Record<string, unknown>,
      newPathname?: string,
    ) => {
      const params = new URLSearchParams([...(searchParams ?? [])]);
      params.set(key, hashObject(value) ?? "");
      const searchString = params.size > 0 ? `?${params.toString()}` : "";
      navigate(`${newPathname ?? pathname}${searchString}`);
    },
    [navigate, searchParams, pathname],
  );

  const removeOneByKey = React.useCallback(
    (key: string, newPathname?: string) => {
      const params = new URLSearchParams([...(searchParams ?? [])]);
      params.delete(key);
      const searchString = params.size > 0 ? `?${params.toString()}` : "";
      navigate(`${newPathname ?? pathname}${searchString}`);
    },
    [navigate, searchParams, pathname],
  );

  const removeMultiple = React.useCallback(
    (keys: ""[], newPathname?: string) => {
      const params = new URLSearchParams([...(searchParams ?? [])]);
      keys.forEach(key => params.delete(key));
      const searchString = params.size > 0 ? `?${params.toString()}` : "";
      navigate(`${newPathname ?? pathname}${searchString}`);
    },
    [navigate, searchParams, pathname],
  );

  const getByKey = React.useCallback(
    <T = Record<string, unknown>>(
      key: SearchParamKey,
      options?: { hashed?: boolean },
    ) => {
      const { hashed } = options ?? {};
      const params = new URLSearchParams([...(searchParams ?? [])]);
      const value = params.get(key) ?? "";
      return hashed ? decodeHashedObject<T>(value) : params.get(key);
    },
    [searchParams],
  );

  return { removeOneByKey, append, getByKey, set, setHashed, removeMultiple };
}

export default useClientQueryString;
